import { useEffect, useMemo, useState } from "react";
import { tr } from "date-fns/locale";
import { Plus, Camera, MapPin, Phone, Trash2, CheckCircle2, FileInput } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { SHOOT_STATUS, ymd } from "@/lib/shootOptions";
import { PortfolioForm } from "@/components/portfolio/PortfolioForm";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const EMPTY = { title: "", address: "", time: "10:00", contact_name: "", contact_phone: "", notes: "" };

const ShootDialog = ({ open, date, onClose, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  useEffect(() => { if (open) setF(EMPTY); }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { time, ...rest } = f;
      await api.post("/shoots", { ...rest, scheduled_at: `${ymd(date)}T${time}` });
      toast.success("Çekim randevusu eklendi");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Çekim Randevusu · {date?.toLocaleDateString("tr-TR")}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Başlık *</Label><Input data-testid="shoot-title-input" required minLength={2} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Örn. Kadıköy 3+1 daire çekimi" /></div>
          <div><Label>Adres *</Label><Input data-testid="shoot-address-input" required minLength={3} value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Saat</Label><Input data-testid="shoot-time-input" type="time" required value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} /></div>
            <div><Label>Mal sahibi</Label><Input data-testid="shoot-contact-name" value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} /></div>
            <div><Label>Telefon</Label><Input data-testid="shoot-contact-phone" value={f.contact_phone} onChange={(e) => setF({ ...f, contact_phone: e.target.value })} /></div>
          </div>
          <div><Label>Not</Label><Textarea data-testid="shoot-notes-input" rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
          <div className="flex justify-end gap-2 pt-2"><Button type="button" variant="outline" onClick={onClose}>İptal</Button><Button data-testid="shoot-save-btn" type="submit" className="bg-emerald-600 hover:bg-emerald-700">Kaydet</Button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ShootCard = ({ s, onDone, onConvert, onDelete }) => {
  const st = SHOOT_STATUS[s.status];
  return (
    <Card data-testid={`shoot-card-${s.id}`} className="p-4 flex flex-col md:flex-row md:items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-slate-900">{s.title}</span>
          <Badge variant="outline" data-testid={`shoot-status-${s.id}`} className={st.cls}>{st.label}</Badge>
        </div>
        <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-4 gap-y-1">
          <span>{new Date(s.scheduled_at).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" })}</span>
          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{s.address}</span>
          {s.contact_name && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{s.contact_name} {s.contact_phone}</span>}
          <span>Oluşturan: {s.created_by_name}</span>
        </div>
      </div>
      <div className="flex gap-2">
        {s.status === "PLANNED" && <Button size="sm" variant="outline" data-testid={`shoot-done-btn-${s.id}`} onClick={() => onDone(s)}><CheckCircle2 className="w-4 h-4 mr-1" />Çekildi</Button>}
        {s.status === "SHOT" && <Button size="sm" data-testid={`shoot-convert-btn-${s.id}`} className="bg-emerald-600 hover:bg-emerald-700" onClick={() => onConvert(s)}><FileInput className="w-4 h-4 mr-1" />Bilgileri gir → Portföy</Button>}
        {s.status !== "COMPLETED" && <Button size="icon" variant="ghost" data-testid={`shoot-delete-btn-${s.id}`} onClick={() => onDelete(s)}><Trash2 className="w-4 h-4 text-red-600" /></Button>}
      </div>
    </Card>
  );
};

export default function ShootsPage() {
  const [shoots, setShoots] = useState([]);
  const [day, setDay] = useState(new Date());
  const [creating, setCreating] = useState(false);
  const [converting, setConverting] = useState(null);
  const load = () => api.get("/shoots").then((r) => setShoots(r.data));
  useEffect(() => { load(); }, []);

  const busyDays = useMemo(() => shoots.filter((s) => s.status !== "COMPLETED").map((s) => new Date(s.scheduled_at)), [shoots]);
  const dayList = shoots.filter((s) => s.status !== "SHOT" && s.scheduled_at.slice(0, 10) === ymd(day));
  const pending = shoots.filter((s) => s.status === "SHOT");

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const cardProps = {
    onDone: (s) => act(() => api.put(`/shoots/${s.id}/done`), "Çekim tamamlandı, şimdi portföy bilgilerini girin"),
    onConvert: setConverting,
    onDelete: (s) => window.confirm("Randevu silinsin mi?") && act(() => api.delete(`/shoots/${s.id}`), "Randevu silindi"),
  };

  return (
    <div className="space-y-6" data-testid="shoots-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Çekim Günü</h1>
          <p className="text-slate-500 text-sm mt-1">Portföy çekimlerini planlayın; çekim bitince bilgileri girip tek tıkla portföye aktarın.</p>
        </div>
        <Button data-testid="new-shoot-btn" className="bg-slate-900" onClick={() => setCreating(true)}><Plus className="w-4 h-4 mr-1" />{day.toLocaleDateString("tr-TR")} için randevu</Button>
      </div>

      <div className="grid lg:grid-cols-[auto_1fr] gap-6 items-start">
        <Card className="p-3">
          <Calendar data-testid="shoots-calendar" mode="single" locale={tr} selected={day} onSelect={(d) => d && setDay(d)}
            modifiers={{ busy: busyDays }} modifiersClassNames={{ busy: "font-bold text-emerald-700 underline underline-offset-4" }} />
        </Card>
        <div className="space-y-6">
          {pending.length > 0 && (
            <section className="space-y-2" data-testid="shoots-pending-section">
              <h2 className="text-base md:text-lg font-semibold text-amber-800">Bilgi girişi bekleyen çekimler ({pending.length})</h2>
              {pending.map((s) => <ShootCard key={s.id} s={s} {...cardProps} />)}
            </section>
          )}
          <section className="space-y-2" data-testid="shoots-day-section">
            <h2 className="text-base md:text-lg font-semibold text-slate-900">{day.toLocaleDateString("tr-TR", { dateStyle: "full" })}</h2>
            {dayList.length === 0 ? (
              <Card className="p-10 text-center text-slate-500 text-sm"><Camera className="w-8 h-8 mx-auto mb-2 text-slate-300" />Bu gün için çekim yok</Card>
            ) : dayList.map((s) => <ShootCard key={s.id} s={s} {...cardProps} />)}
          </section>
        </div>
      </div>

      <ShootDialog open={creating} date={day} onClose={() => setCreating(false)} onSaved={load} />
      <PortfolioForm open={!!converting} onClose={() => setConverting(null)} onSaved={load}
        createUrl={converting ? `/shoots/${converting.id}/complete` : undefined}
        initial={converting ? { title: converting.title, description: [converting.address, converting.notes].filter(Boolean).join("\n") } : undefined} />
    </div>
  );
}
